import { dictionaryApi } from './dictionaryClient';
import { RUP_TRIPLE, RUP_VIBHAKTI, type RupDeclensionKey } from './rupApi';
import type { SubantaLinga, SubantaPrakar } from './subantaApi';

const ROWS = 8;
const COLS = 3;

export type SubantaRupFormulaCellKey = `v_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8}_${1 | 2 | 3}`;

export const SUBANTA_RUP_FORMULA_CELL_KEYS: SubantaRupFormulaCellKey[] = (() => {
  const keys: SubantaRupFormulaCellKey[] = [];
  for (let r = 1; r <= ROWS; r++) {
    for (let c = 1; c <= COLS; c++) {
      keys.push(`v_${r}_${c}` as SubantaRupFormulaCellKey);
    }
  }
  return keys;
})();

export type SubantaRupFormulaScalars = {
  prakar: SubantaPrakar;
  linga: SubantaLinga;
  roop_prakar?: string | null;
  aadhar_shabda?: string | null;
  vishesh?: string | null;
  bat?: string | null;
  natwa: boolean;
  shatwa: boolean;
} & Partial<Record<SubantaRupFormulaCellKey, string | null | undefined>>;

export type SubantaRupFormula = SubantaRupFormulaScalars & {
  id: number;
  createdAt?: string;
  updatedAt?: string;
  created_at?: string;
  updated_at?: string;
};

export type CreateSubantaRupFormulaBody = {
  prakar: SubantaPrakar;
  linga: SubantaLinga;
  roop_prakar?: string | null;
  aadhar_shabda?: string | null;
  vishesh?: string | null;
  bat?: string | null;
  natwa?: boolean;
  shatwa?: boolean;
} & Partial<Record<SubantaRupFormulaCellKey, string | null>>;

export type PatchSubantaRupFormulaBody = Partial<CreateSubantaRupFormulaBody>;

function normalizeList(data: unknown): SubantaRupFormula[] {
  if (Array.isArray(data)) return data as SubantaRupFormula[];
  if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: SubantaRupFormula[] }).data;
  }
  return [];
}

/** Build POST body from form values; omits empty optional grid cells. */
export function createBodyFromFormValues(
  values: SubantaRupFormulaFormValues
): CreateSubantaRupFormulaBody {
  const body: CreateSubantaRupFormulaBody = {
    prakar: values.prakar,
    linga: values.linga,
    natwa: values.natwa ?? false,
    shatwa: values.shatwa ?? false,
  };
  const roop = values.roop_prakar?.trim();
  if (roop) body.roop_prakar = roop;
  const aadhar = values.aadhar_shabda?.trim();
  if (aadhar) body.aadhar_shabda = aadhar;
  if (values.vishesh !== undefined) {
    body.vishesh = values.vishesh;
  }
  const batTrim = values.bat?.trim();
  body.bat = batTrim === undefined || batTrim === '' ? null : batTrim;

  for (const key of SUBANTA_RUP_FORMULA_CELL_KEYS) {
    const cell = values[key]?.trim();
    if (cell) body[key] = cell;
  }
  return body;
}

export type SubantaRupFormulaFormValues = {
  prakar: SubantaPrakar;
  linga: SubantaLinga;
  roop_prakar?: string;
  aadhar_shabda?: string;
  vishesh?: string;
  bat?: string;
  natwa: boolean;
  shatwa: boolean;
} & Record<SubantaRupFormulaCellKey, string | undefined>;

export function formulaToFormValues(row: SubantaRupFormula): SubantaRupFormulaFormValues {
  const cells = {} as Record<SubantaRupFormulaCellKey, string | undefined>;
  for (const key of SUBANTA_RUP_FORMULA_CELL_KEYS) {
    const v = row[key];
    cells[key] = v == null ? undefined : String(v);
  }
  return {
    prakar: row.prakar,
    linga: row.linga,
    roop_prakar: row.roop_prakar ?? undefined,
    aadhar_shabda: row.aadhar_shabda ?? undefined,
    vishesh: row.vishesh ?? undefined,
    bat: row.bat ?? undefined,
    natwa: row.natwa ?? false,
    shatwa: row.shatwa ?? false,
    ...cells,
  };
}

function normCell(v: string | null | undefined): string | null {
  if (v == null) return null;
  const t = String(v).trim();
  return t === '' ? null : t;
}

export function buildSubantaRupFormulaPatch(
  prev: SubantaRupFormula,
  values: SubantaRupFormulaFormValues
): PatchSubantaRupFormulaBody {
  const patch: PatchSubantaRupFormulaBody = {};
  if (values.prakar !== prev.prakar) patch.prakar = values.prakar;
  if (values.linga !== prev.linga) patch.linga = values.linga;
  if ((values.natwa ?? false) !== (prev.natwa ?? false)) patch.natwa = values.natwa ?? false;
  if ((values.shatwa ?? false) !== (prev.shatwa ?? false)) patch.shatwa = values.shatwa ?? false;

  const roopNext = values.roop_prakar?.trim() ?? '';
  const roopPrev = (prev.roop_prakar ?? '').trim();
  if (roopNext !== roopPrev) patch.roop_prakar = roopNext === '' ? null : roopNext;

  const aadharNext = values.aadhar_shabda?.trim() ?? '';
  const aadharPrev = (prev.aadhar_shabda ?? '').trim();
  if (aadharNext !== aadharPrev) patch.aadhar_shabda = aadharNext === '' ? null : aadharNext;

  const visheshNext = values.vishesh ?? '';
  const visheshPrev = prev.vishesh ?? '';
  if (visheshNext !== visheshPrev) patch.vishesh = visheshNext;

  const batNext = normCell(values.bat);
  const batPrev = normCell(prev.bat);
  if (batNext !== batPrev) patch.bat = batNext;

  for (const key of SUBANTA_RUP_FORMULA_CELL_KEYS) {
    const next = normCell(values[key]);
    const prevV = normCell(prev[key] as string | null | undefined);
    if (next !== prevV) {
      patch[key] = next;
    }
  }
  return patch;
}

export async function listSubantaRupFormulas(): Promise<SubantaRupFormula[]> {
  const res = await dictionaryApi.get<unknown>('/subanta-rup-formula');
  return normalizeList(res.data);
}

export async function getSubantaRupFormula(id: number): Promise<SubantaRupFormula> {
  const res = await dictionaryApi.get<SubantaRupFormula>(`/subanta-rup-formula/${id}`);
  return res.data;
}

export async function createSubantaRupFormula(
  body: CreateSubantaRupFormulaBody
): Promise<SubantaRupFormula> {
  const res = await dictionaryApi.post<SubantaRupFormula>('/subanta-rup-formula', body);
  return res.data;
}

export async function updateSubantaRupFormula(
  id: number,
  body: PatchSubantaRupFormulaBody
): Promise<SubantaRupFormula> {
  const res = await dictionaryApi.patch<SubantaRupFormula>(`/subanta-rup-formula/${id}`, body);
  return res.data;
}

export async function deleteSubantaRupFormula(id: number): Promise<void> {
  await dictionaryApi.delete(`/subanta-rup-formula/${id}`);
}

/**
 * Map formula grid `v_R_C` (row = vibhakti order) to rup declension field names.
 * When `baseWord` is set, each cell is `baseWord + formulaCell` (subanta stem first, then formula segment).
 */
export function subantaRupFormulaGridToRupDeclensionFields(
  formula: SubantaRupFormula,
  baseWord?: string | null
): Partial<Record<RupDeclensionKey, string>> {
  const base = baseWord != null ? String(baseWord).trim() : '';
  const out: Partial<Record<RupDeclensionKey, string>> = {};
  for (let r = 1; r <= 8; r++) {
    for (let c = 1; c <= 3; c++) {
      const fk = `v_${r}_${c}` as SubantaRupFormulaCellKey;
      const raw = formula[fk];
      const v = RUP_VIBHAKTI[r - 1];
      const t = RUP_TRIPLE[c - 1];
      const rk = `${v}_${t}` as RupDeclensionKey;
      if (raw != null && String(raw).trim() !== '') {
        const part = String(raw).trim();
        out[rk] = base ? `${base}${part}` : part;
      }
    }
  }
  return out;
}

export type SearchSubantaRupFormulaParams = {
  prakar: SubantaPrakar;
  linga: SubantaLinga;
  natwa: boolean;
  shatwa: boolean;
  /**
   * Omitted = do not send `bat` (ignore bat when matching).
   * Empty string = send `bat=` to match formula rows where `bat` is null.
   * Non-empty = exact `bat` match.
   */
  bat?: string | null;
};

export async function searchSubantaRupFormula(
  params: SearchSubantaRupFormulaParams
): Promise<SubantaRupFormula[]> {
  const q: Record<string, string | number> = {
    prakar: params.prakar,
    linga: params.linga,
    natwa: params.natwa ? 1 : 0,
    shatwa: params.shatwa ? 1 : 0,
  };
  if (params.bat !== undefined) {
    q.bat = params.bat === null || params.bat === '' ? '' : params.bat;
  }
  const res = await dictionaryApi.get<unknown>('/subanta-rup-formula/search', { params: q });
  return normalizeList(res.data);
}
